import { useMutation } from '@provision/graphql-client';
import { useCallback } from 'react';
import { PartnerHolidayEntity } from 'src/graphql/entities/partner-holiday/partner-holiday.types';
import { DELETE_PARTNER_HOLIDAY_QUERY } from 'src/graphql/entities/partner-holiday/partner-holidays.operations';
import { ConditionInput } from 'src/graphql/types';
import { useNotifications } from 'src/utils/hooks';

export interface DeletePartnerHolidayMutation {
  deletePartnerHoliday: Pick<PartnerHolidayEntity, 'id'>[];
}

export type DeletePartnerHolidayMutationParams = {
  conditions: ConditionInput[];
};

export const useDeletePartnerHoliday = () => {
  const { showErrorAlert } = useNotifications();
  const [deletePartnerHolidayMutation, { loading: isDeleting }] = useMutation<
    DeletePartnerHolidayMutation,
    DeletePartnerHolidayMutationParams
  >(DELETE_PARTNER_HOLIDAY_QUERY, {
    update(cache, { data }) {
      if (!data?.deletePartnerHoliday?.length) {
        return;
      }
      data.deletePartnerHoliday.forEach((email) => {
        const id = cache.identify({
          __typename: 'PartnerHoliday',
          id: email.id,
        });
        cache.evict({ id });
      });
      cache.gc();
    },
    onError: (error) => {
      console.error(error);
      showErrorAlert('Failed to deleting holiday entity, please try again');
    },
  });

  const deletePartnerHolidaysById = useCallback(
    async (partnerHolidayIds: string[]) => {
      await deletePartnerHolidayMutation({
        variables: {
          conditions: [
            {
              attribute: 'partnerHolidayId',
              operator: 'in',
              value: partnerHolidayIds.join(','),
            },
          ],
        },
        optimisticResponse: {
          deletePartnerHoliday: partnerHolidayIds.map((id) => ({
            __typename: 'PartnerHoliday',
            id,
          })),
        },
      });
    },
    [deletePartnerHolidayMutation],
  );

  return {
    deletePartnerHolidaysById,
    isDeleting,
  };
};
